import { createSlice, CaseReducer, PayloadAction } from '@reduxjs/toolkit';
import { Toast } from '@10d/tend-ui/primitives/Toast';

import { get, post } from 'api/requests';

import { DELETE } from 'consts';

import { AppThunk } from 'store/types';
import { IParamsReq } from 'types';
import {
  EngineeringSettingsStore,
  EngineeringSettingsObjects,
  EngineeringSettings,
  EnDevDocs,
} from 'features/Setting/EngineeringSetting/types';

const initialState: EngineeringSettingsStore = {
  loading: false,
  projectList: [],
  objectList: [],
  enDevDocs: {
    loading: false,
    data: {} as EnDevDocs,
  },
};

const reset: CaseReducer<EngineeringSettingsStore> = () => initialState;

const loading: CaseReducer<EngineeringSettingsStore, PayloadAction<boolean>> = (state, action) => {
  state.loading = action.payload;
};

const projectList: CaseReducer<EngineeringSettingsStore, PayloadAction<EngineeringSettings[]>> = (
  state,
  action,
) => {
  state.projectList = action.payload;
};

const objectList: CaseReducer<
  EngineeringSettingsStore,
  PayloadAction<EngineeringSettingsObjects[]>
> = (state, action) => {
  state.objectList = action.payload;
};

const enDevDocs: CaseReducer<EngineeringSettingsStore, PayloadAction<EnDevDocs>> = (
  state,
  action,
) => {
  state.enDevDocs.data = action.payload;
};

const loadingEnDevDocs: CaseReducer<EngineeringSettingsStore, PayloadAction<boolean>> = (
  state,
  action,
) => {
  state.enDevDocs.loading = action.payload;
};

export const engineeringSettingSlice = createSlice({
  name: 'engineeringSetting',
  initialState,
  reducers: {
    reset,
    loading,
    projectList,
    objectList,
    loadingEnDevDocs,
    enDevDocs,
  },
});

const {
  reset: setReset,
  loading: setLoading,
  projectList: setProjectSettings,
  objectList: setObjectSetting,
  loadingEnDevDocs: setLoadingEnDevDocs,
  enDevDocs: setEnDevDocs,
} = engineeringSettingSlice.actions;

const setSettings =
  (settings: EngineeringSettings[]): AppThunk =>
  dispatch => {
    dispatch(setProjectSettings(settings));
  };

export const getSettings =
  (project: number): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<EngineeringSettings[]>(`/api/standards/custom-inspection-type/?project=${project}`)
      .then(({ data }) => {
        dispatch(setSettings(data));
        dispatch(getEnDevDocs(project));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getSettingByObject =
  (search: IParamsReq): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    get<EngineeringSettingsObjects[]>(
      '/api/standards/custom-inspection-type/list-by-structure',
      search,
    )
      .then(({ data }) => {
        dispatch(setObjectSetting(data));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const setSetting =
  (params: {
    project: number;
    structure: number;
    network_type: number;
    inspection_type: string;
  }): AppThunk =>
  dispatch => {
    post('/api/standards/custom-inspection-type/', params).then(() => {
      dispatch(getSettingByObject({ project: params.project, structure: params.structure }));
    });
  };

export const setTimeNetDelivery =
  (
    search: IParamsReq,
    data: {
      project: number | null;
      duration: number;
      networkType: string | null;
      network?: number;
    },
  ): AppThunk =>
  dispatch => {
    post('/api/timesheet/en-inspection-time/', data).then(() => {
      if (data.network) {
        dispatch(getSettingByObject(search));
      } else {
        dispatch(getSettings(Number(search.project)));
      }
    });
  };

export const setSwitch =
  (params: { project: number; active: boolean; networkType: string }): AppThunk =>
  dispatch => {
    post('/api/standards/en-active/', params).then(() => {
      dispatch(getSettings(params.project));
    });
  };

export const updateEngineeringWorker =
  (
    params: {
      projects: number[];
      types: number[];
      users: number[];
      isSign?: boolean;
      isCheck?: boolean;
    },
    project: number,
    actionType?: string,
  ): AppThunk =>
  dispatch => {
    dispatch(setLoading(true));

    post('/api/abilities/multi_update_workers/', params)
      .then(() => {
        if (actionType === DELETE) {
          Toast.success({ message: 'Сотрудник удален' });
        } else {
          Toast.success({ message: 'Сотрудники добавлены' });
        }
      })
      .then(() => {
        dispatch(getSettings(Number(project)));
      })
      .finally(() => dispatch(setLoading(false)));
  };

export const getEnDevDocs =
  (id: number): AppThunk =>
  dispatch => {
    dispatch(setLoadingEnDevDocs(true));
    get<EnDevDocs>(`/api/standards/en-development/get-by-project/${id}`)
      .then(({ data }) => {
        dispatch(setEnDevDocs(data));
      })
      .finally(() => dispatch(setLoadingEnDevDocs(false)));
  };

export const changeEnDevDocs =
  (params: EnDevDocs): AppThunk =>
  dispatch => {
    dispatch(setLoadingEnDevDocs(true));

    post('/api/standards/en-development/create-or-update/', params).finally(() =>
      dispatch(setLoadingEnDevDocs(false)),
    );
  };

export { setReset };
